import React, { createContext, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPublicSettings } from "../api/settings";
import { getPublicSiteConfiguration } from "../api/PublicSiteConfiguration";
import { resolveMediaUrl } from "../utils/mediaUrl";

const SettingsContext = createContext({});

export function SettingsProvider({ children }) {
  const { data: settings = [] } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => getPublicSettings().then(r => r.data),
  });

  // Convert array of {key, value} into a plain object for easy access
  const settingsMap = Array.isArray(settings)
    ? settings.reduce((acc, s) => ({ ...acc, [s.key]: s.value }), {})
    : settings;

  // Separate endpoint for logo/favicon — /api/public/site-configuration/,
  // not part of the key/value settings list above.
  const { data: siteConfig } = useQuery({
    queryKey: ["public-site-configuration"],
    queryFn: () => getPublicSiteConfiguration().then(r => r.data),
    staleTime: 10 * 60 * 1000, // rarely changes; avoid refetching on every mount
  });

  const logoUrl = resolveMediaUrl(
    siteConfig?.organization_logo_url || siteConfig?.organization_logo
  );
  const faviconUrl = resolveMediaUrl(
    siteConfig?.favicon_url || siteConfig?.favicon
  );

  const value = {
    ...settingsMap,
    ...(logoUrl ? { logo_url: logoUrl } : {}),
    ...(faviconUrl ? { favicon_url: faviconUrl } : {}),
  };

  // Favicons live on a <link> tag in <head>, not in the React tree, so they
  // have to be applied imperatively once the URL resolves.
  useEffect(() => {
    if (!faviconUrl) return;

    // Swap in a fresh <link> rather than mutating href on an existing one —
    // Chrome in particular caches the old icon and ignores a bare href
    // change on the same node until a hard refresh.
    document.querySelectorAll("link[rel~='icon']").forEach((el) => el.remove());

    const link = document.createElement("link");
    link.rel = "icon";
    link.href = faviconUrl;
    document.head.appendChild(link);
  }, [faviconUrl]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);